import React, { useEffect, useState } from "react";
import {
  Filter,
  Download,
  ChevronDown,
  BarChart3,
  RefreshCw,
  FileText,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  // CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  // Legend,
} from "recharts";
import {
  exportStreamViewersReportCSV,
  exportStreamViewersReportExcel,
  exportStreamViewersReportPDF,
  getStreamViewersReport,
} from "../../../api/reportAPI";
import { toast } from "react-toastify";
import logger from "../../../utils/logger";
import DateInput from "react-calendar-ui-kit";
import { toISOForDB,formatDisplayDate } from "../../../utils/Date";


type FilterType = "today" | "week" | "month" | "year" | "custom";

const StreamViewers: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState<FilterType>("today");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<any[]>([]);
  const [resetLoading, setResetLoading] = useState(false);
  const [totals, setTotals] = useState({
    totalAdvocate: 0,
    totalIndividual: 0,
    total: 0,
  });

  const fetchReport = async () => {
    try {
      setLoading(true);

      const params: any = { filter: selectedFilter };
      if (selectedFilter === "custom" && from && to) {
        params.fromDate = from;
        params.toDate = to;
      }

      const response = await getStreamViewersReport(params);
      const data = response?.data?.data;
      logger.log("check stream viewers data", data);

      if (!data) return;

      let graph = (data.graphData || []).map((g: any) => ({
        name: g.name,
        advocate: Number(g.advocate || 0),
        individual: Number(g.individual || 0),
      }));

      // 🔹 single point → flat line fix
      if (graph.length === 1) {
        graph = [graph[0], { ...graph[0] }];
      }

      setGraphData(graph);

      setTotals({
        totalAdvocate: data.totalAdvocates || 0,
        totalIndividual: data.totalIndividuals || 0,
        total: data.totalViewers || 0,
      });
    } catch (err) {
      logger.error("Error fetching stream viewers report:", err);
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  //  Fetch whenever filter/date changes
  useEffect(() => {
    if (selectedFilter === "custom") return; //

    fetchReport();
  }, [selectedFilter]);

  //  Apply Filters
  const handleApplyFilters = () => {
    if (!from || !to) {
      setError("Please select both From and To dates");

      setGraphData([]); // ✅ ensure empty
      setTotals({
        totalAdvocate: 0,
        totalIndividual: 0,
        total: 0,
      });

      return;
    }

    setError("");
    fetchReport(); // ✅ ONLY place where custom API is called
  };

  //  Reset Filters
  const handleReset = async () => {
    try {
      setResetLoading(true);

      setSelectedFilter("today");
      setFrom("");
      setTo("");
      setError("");
    } catch (error) {
      logger.error("err", error);
    } finally {
      setResetLoading(false);
    }
  };
  //  Handle Export
  const handleExport = async (type: "csv" | "excel" | "pdf") => {
    setExportLoading(type);
    try {
      const params: any = { filter: selectedFilter };

      if (selectedFilter === "custom") {
        params.fromDate = toISOForDB(from);
        params.toDate = toISOForDB(to);
      }

      let apiFunc;
      if (type === "csv") apiFunc = exportStreamViewersReportCSV;
      else if (type === "excel") apiFunc = exportStreamViewersReportExcel;
      else apiFunc = exportStreamViewersReportPDF;

      const res = await apiFunc(params);
      if (!res || !res.data) {
        toast.error("No data to export");
        return;
      }

      const contentType =
     res.headers["content-type"] != null
     ? String(res.headers["content-type"])
     : undefined;

     const blob = new Blob([res.data], { type: contentType });


      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      const extension = type === "excel" ? "xlsx" : type; // ⭐ FIX
      a.href = url;
      a.download = `stream-viewers-report.${extension}`;

      a.click();
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded ${extension.toUpperCase()} report`);
    } catch (err) {
      toast.error("Failed to export report");
      logger.error("err", err);
    } finally {
      setExportLoading(null);
    }
  };

  return (
    <div className="min-h-screen max-w-screen p-4">
      {/* Filters */}
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          </div>

          {/* Filter Buttons + Export Button Row */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-3">
              {["today", "custom", "week", "month", "year"].map((f) => (
                <label
                  key={f}
                  className={`px-4 py-2 rounded-lg border cursor-pointer transition-colors
              ${
                selectedFilter === f
                  ? "bg-indigo-50 border-indigo-500 text-indigo-700 font-medium"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
                >
                  <input
                    type="radio"
                    name="filter"
                    value={f}
                    checked={selectedFilter === f}
                    onChange={(e) => {
                      const value = e.target.value as FilterType;
                      setSelectedFilter(value);
                      setError("");

                      if (value === "custom") {
                        setFrom("");
                        setTo("");
                        setGraphData([]); // ✅ clear graph
                        setTotals({
                          totalAdvocate: 0,
                          totalIndividual: 0,
                          total: 0,
                        });
                      }
                    }}
                    className="hidden"
                  />
                  {f === "today"
                    ? "Today"
                    : f === "week"
                      ? "This Week"
                      : f === "month"
                        ? "This Month"
                        : f === "year"
                          ? "This Year"
                          : "Date Range"}
                </label>
              ))}
            </div>

            {/* Export Button - Always Visible */}
            <div className="relative">
              <button
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none"
                onClick={() => {
                  if (exportLoading) return;
                  const dropdown = document.getElementById("export-dropdown");
                  dropdown?.classList.toggle("hidden");
                }}
              >
                {exportLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Export Report
                    <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </button>

              <div
                id="export-dropdown"
                className="hidden absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
              >
                <div className="py-1">
                  {["csv", "excel", "pdf"].map((type) => (
                    <button
                      key={type}
                      onClick={() => handleExport(type as any)}
                      disabled={exportLoading === type}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                      {exportLoading === type ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      {`Download as ${type.toUpperCase()}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Custom Date Range Fields */}
          {selectedFilter === "custom" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 ">
              <div className="space-y-2">
                <label  htmlFor= "FromDate" className="block text-sm font-medium text-gray-700">
                  From Date
                </label>
                <DateInput
                  value={from}
                  onChange={(val: string) => setFrom(val)}
                  max={to}
                />
              </div>

              <div className="space-y-2">
                <label  htmlFor="toDate" className="block text-sm font-medium text-gray-700">
                  To Date
                </label>
                <DateInput value={to} onChange={(val: string) => setTo(val)} />
              </div>

              <div className="flex items-end space-x-2">
                <button
                  onClick={handleApplyFilters}
                  disabled={loading}
                  className="w-48 inline-flex items-center px-6 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Filter className="w-4 h-4 mr-2" />
                  )}
                  {loading ? "Applying..." : "Apply Filters"}
                </button>
                <button
                  onClick={handleReset}
                  disabled={resetLoading}
                  className={`inline-flex items-center px-6 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors
                                                                   ${resetLoading ? "opacity-50 cursor-not-allowed" : "opacity-100 cursor-pointer"}`}
                >
                  <RefreshCw
                    className={`w-4 h-4 mr-2 ${resetLoading ? "animate-spin" : ""}`}
                  />
                  Reset
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
          Stream Viewers Overview
        </h3>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : graphData.length ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={graphData}>
              <XAxis
                dataKey="name"
                padding={{ left: 40, right: 40 }}
                tick={{ fontSize: 12 }}
                tickFormatter={(value: string) =>
                 value.includes(" ") ? value.split(" ")[1] : formatDisplayDate(value)
                }
                label={{
                  value: "Date",
                  position: "insideBottom",
                  offset: -5,
                }}
              />

              <YAxis
                allowDecimals={false}
                domain={[0, "dataMax + 1"]}
                label={{
                  value: "No. of Viewers",
                  angle: -90,
                  position: "insideLeft",
                }}
              />

              <Tooltip
                cursor={{ stroke: "#9CA3AF", strokeWidth: 1 }}
                contentStyle={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  padding: "10px 14px",
                }}
                labelStyle={{
                  fontWeight: 600,
                  marginBottom: 6,
                }}
                itemStyle={{ fontSize: 14 }}
                formatter={(value, name) => {
                  const safeValue = typeof value === "number" ? value : 0;

                  if (name === "advocate") {
                    return [safeValue, "Advocates"];
                  }

                  if (name === "individual") {
                    return [safeValue, "Individuals"];
                  }

                  return [safeValue, name];
                }}
                labelFormatter={(label) => {
                  if (typeof label === "string") {
                   return label.includes(" ") ? label : formatDisplayDate(label);
                  }
                  return label;
                }}
              />

              <Line
                type="monotone"
                dataKey="advocate"
                name="advocate"
                stroke="#4F46E5"
                strokeWidth={3}
                dot={{ r: 6 }}
                activeDot={{ r: 9 }}
              />

              <Line
                type="monotone"
                dataKey="individual"
                name="individual"
                stroke="#10B981"
                strokeWidth={3}
                dot={{ r: 6 }}
                activeDot={{ r: 9 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex flex-col items-center justify-center space-y-3">
            <FileText className="h-12 w-12 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-600 mb-0">
              No Data Found
            </h3>
            <p className="text-xs text-gray-400">
              Try adjusting your filters to see more results
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 text-center">
          <div className="p-4 bg-indigo-50 rounded-lg">
            <p className="text-sm text-gray-500">Advocates</p>
            <p className="text-2xl font-bold text-indigo-700">
              {totals.totalAdvocate}
            </p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-gray-500">Individuals</p>
            <p className="text-2xl font-bold text-green-700">
              {totals.totalIndividual}
            </p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-500">Total Viewers</p>
            <p className="text-2xl font-bold text-blue-700">{totals.total}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StreamViewers;
